import type {
  WebContainerProcess,
  FileSystemTree,
  DirectoryNode,
  FileNode,
} from '@webcontainer/api'
import type { WebContainer } from '@webcontainer/api'
import { create } from 'zustand'

import type { Project } from '@/lib/types'
import { AUTO_FIX_ISSUE_TIME_LIMIT, SCRATCHPAD_FILE_NAME } from '@/lib/constant'
import { useProjectStore } from '@/hooks/use-project-store'
import {
  filterSensitiveFiles,
  generateShortId,
  removeAfterfix,
} from '@/lib/utils'
import { alertError } from '@/lib/alert-error'
import _ from 'lodash'
import type { EditorChangeContent } from '@/lib/editor/editor'
import { toast } from 'sonner'

type PreviewState = {
  webContainer?: WebContainer
  apiKey?: string
  stage:
    | 'idle'
    | 'installing'
    | 'starting'
    | 'running'
    | 'error'
    | 'runtime_error'
  error?: string
  previewUrl?: string
  startProcess?: WebContainerProcess
  initPreview: () => Promise<void>
  // updatePreview 需要明确返回错误原因，方便 ai-coding 拿到错误信息后重试
  updatePreview: (options?: {
    skipPreview?: boolean
    useCachedUrl?: boolean
  }) => Promise<{
    isSuccess: boolean
    error?: string
    previewUrl?: string
  }>
  reloadPreviewPage: () => void
  skipVersionCheck: boolean
  setSkipVersionCheck: (skip: boolean) => void
  uploadBuildDist2R2: (
    project: Project,
    // output: Record<string, string>,
    dryRun?: boolean,
  ) => Promise<void>
  buildOutput?: Record<string, string>
  buildUrl?: string
  setBuildUrl: (url: string) => void
  setError: (error: string) => void
  exportProject: () => Promise<void>
  exportFileSystemTree: () => Promise<void>
  processingText?: string
  setProcessingText: (text: string) => void
  updateScreenshot: () => Promise<void>
  updateOpenGraphImage: () => Promise<void>
  showPreview: boolean
  setShowPreview: (show: boolean) => void
  setRuntimeError: (error: string) => void
  setStage: (
    stage:
      | 'idle'
      | 'installing'
      | 'starting'
      | 'running'
      | 'error'
      | 'runtime_error',
  ) => void
  autoFixIssueTime: number
  increaseAutoFixIssueTime: () => void
  canAutoFixIssue: () => boolean
  pendingBuild?: {
    id: string
    version: number
    status: 'building' | 'ready' | 'failed' | 'superseded' | 'idle'
    updatedAt?: string | null
    error?: string | null
  }
  clientOverrides: EditorChangeContent[]
  overrideApplyInterval?: number
  waitForBuild: (options: {
    projectId: string
    version: number
    buildId: string
    pollIntervalMs?: number
    maxAttempts?: number
    skipReload?: boolean
  }) => Promise<void>
  rebuildCurrentVersion: (
    projectId: string,
    versionIndex: number,
  ) => Promise<void>
  setClientOverrides: (overrides: EditorChangeContent[]) => void
  applyClientOverrides: () => void
  clearClientOverrides: () => void
  clearPendingBuild: () => void
}

export const usePreviewStore = create<PreviewState>()((set, get) => ({
  stage: 'idle',
  showPreview: true,
  clientOverrides: [],
  skipVersionCheck: false,
  setSkipVersionCheck: (skip: boolean) => set({ skipVersionCheck: skip }),
  initPreview: async () => {
    set({ stage: 'idle' })
    // if (get().webContainer) return
    // if (process.env.NEXT_PUBLIC_WEBCONTAINER_API_KEY) {
    //   auth.init({
    //     clientId: process.env.NEXT_PUBLIC_WEBCONTAINER_API_KEY,
    //     scope: '',
    //   })
    // }
    // const webContainer = await WebContainer.boot({
    //   coep: 'credentialless',
    // })
    // webContainer.on('error', (error) => {
    //   console.error(error)
    //   set({ stage: 'error', error: error.message })
    //   useProjectStore.getState().refundCodingRequest()
    // })
    // webContainer.on('server-ready', (_, url) => {
    //   set({ stage: 'running', previewUrl: url })
    // })
    // set({ webContainer })
  },
  updatePreview: async ({
    useCachedUrl = false,
    skipPreview = false,
  }: {
    useCachedUrl?: boolean
    skipPreview?: boolean
  } = {}): Promise<{
    isSuccess: boolean
    previewUrl?: string
    error?: string
  }> => {
    // 反馈结果
    const result = {
      isSuccess: true,
      error: '',
      previewUrl: '',
    }

    if (skipPreview === false) {
      set({ stage: 'starting' })
    }
    const { project, setProject, getFileSystemTree } =
      useProjectStore.getState()
    // Fast path: use cached URL if available and useCachedUrl is true
    if (useCachedUrl) {
      const version = project?.versions?.[project?.file_system_tree_index]
      if (version?.previewUrl) {
        result.previewUrl = version.previewUrl
        set({ stage: 'running', previewUrl: version.previewUrl })
        return result
      }
    }

    const tree = getFileSystemTree()
    if (!project || !tree) {
      const error = project ? 'File system tree not found' : 'Project not found'
      set({ stage: 'error', error })
      return {
        isSuccess: false,
        error,
      }
    }
    // Trigger a build
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 60000) // 60 second timeout

    try {
      const response = await fetch(`/api/projects/${project?._id}/build`, {
        method: 'POST',
        body: JSON.stringify({
          versionIndex: project?.file_system_tree_index,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      const resp = await response.json()
      if (resp.status === 'success') {
        if (skipPreview === false) {
          const { versions, previewUrl } = resp
          set({ stage: 'running', previewUrl })
          result.previewUrl = previewUrl
          // NOTE: use the latest version from state
          const projectFromState = useProjectStore.getState().project
          if (projectFromState && versions) {
            setProject({ ...projectFromState, versions })
          }
          if (projectFromState && !projectFromState.thumbnail) {
            setTimeout(() => {
              get().updateScreenshot()
            }, 13_000) // is 13 seconds enough? is there a better way?
          }
        }
      } else {
        set({ stage: 'error', error: resp.error })

        alertError('serve-build error', resp?.error)

        result.error = resp.error
        result.isSuccess = false
      }
    } catch (error: any) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') {
        const errorMsg = 'Build request timed out. Please try again.'
        set({ stage: 'error', error: errorMsg })
        result.error = errorMsg
        result.isSuccess = false
        alertError('Build timeout', errorMsg)
      } else {
        const errorMsg = error.message || 'Failed to build project'
        set({ stage: 'error', error: errorMsg })
        result.error = errorMsg
        result.isSuccess = false
        alertError('Build error', errorMsg)
      }
    }

    return result
  },

  setBuildUrl: (url: string) => {
    set({ buildUrl: url })
  },

  uploadBuildDist2R2: async (
    project: Project,
    // buildOutput: Record<string, string>,
    dryRun = false,
  ) => {
    try {
      // Convert buildOutput to FormData
      const formData = new FormData()

      // Iterate through files in buildOutput
      // for (const [path, content] of Object.entries(buildOutput)) {
      //   // Remove leading /dist/
      //   const relativePath = path.replace(/^\/dist\//, '')
      //   // Create Blob object
      //   const blob = new Blob([content], { type: getContentType(relativePath) })
      //   // Add to FormData, maintaining relative path structure
      //   formData.append('files', blob, relativePath)
      // }

      // Add project ID
      formData.append('projectId', project._id.toString())
      // Add new parameters
      project.project_name &&
        formData.append('projectName', project.project_name)
      project.build_url && formData.append('deploy_url', project.build_url)
      // project.deploy_url && formData.append('deployUrl', project.deploy_url)
      project.thumbnail && formData.append('thumbnail', project.thumbnail)

      // Call backend API
      const response = await fetch('/api/upload-build', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        throw new Error('Failed to upload build files')
      }

      const result = await response.json()
      // Set build access URL
      set({ buildUrl: project.build_url || result.url })
      {
        const { project, setProject } = useProjectStore.getState()
        if (project && !dryRun) {
          setProject({
            ...project,
            deploy_url: result.url,
          })
        }
      }
      return result
    } catch (error) {
      console.error('Failed to upload build files:', error)
      throw error
    }
  },
  setError: (error: string) => {
    set({ stage: 'error', error })
  },
  setRuntimeError: (error: string) => {
    set({ stage: 'runtime_error', error })
  },
  exportProject: async () => {
    const webContainer = get().webContainer
    if (!webContainer) return
    const projectId = useProjectStore.getState().project?._id.toString()
    const data = await webContainer.export('.', { format: 'zip' })
    const zip = new Blob([data as unknown as ArrayBuffer])
    const url = URL.createObjectURL(zip)
    const a = document.createElement('a')
    a.href = url
    a.download = `project_${projectId}.zip`
    a.click()
    URL.revokeObjectURL(url)
  },
  exportFileSystemTree: async () => {
    const project = useProjectStore.getState().project
    if (!project) return
    const projectId = project._id.toString()
    const fileSystemTree = filterSensitiveFiles(project.file_system_tree)
    // Create a new JSZip instance
    const JSZip = (await import('jszip')).default
    const zip = new JSZip()
    // Recursive function to process the file system tree
    const processNode = (node: FileSystemTree, path: string) => {
      for (const [name, content] of Object.entries(node)) {
        // Skip files named SCRATCHPAD_FILE_NAME
        if (name === SCRATCHPAD_FILE_NAME) continue

        const fullPath = path ? `${path}/${name}` : name
        const nodeContent = content as DirectoryNode | FileNode
        if ('directory' in nodeContent) {
          // If it's a directory, recursively process its contents
          processNode(nodeContent.directory, fullPath)
        } else if ('file' in nodeContent) {
          // If it's a file, add it to the zip
          zip.file(fullPath, nodeContent.file.contents)
        }
      }
    }
    // Start processing from root
    processNode(fileSystemTree, '')
    // Generate zip file
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    // Create download link and trigger download
    const url = URL.createObjectURL(zipBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `project_${projectId}.zip`
    a.click()
    URL.revokeObjectURL(url)
  },
  reloadPreviewPage: () => {
    const previewUrl = get().previewUrl
    if (!previewUrl) return

    // Skip version check on user-initiated refresh to avoid unnecessary rebuilds
    set({ skipVersionCheck: true })

    // 使用基础 URL 来查找 iframe（移除可能存在的查询参数）
    const baseUrl = previewUrl.split('?')[0]

    const previewFrame = document.querySelector(
      `iframe[src^="${baseUrl}"]`,
    ) as HTMLIFrameElement
    if (previewFrame) {
      // Hide iframe during reload to prevent black screen
      previewFrame.style.opacity = '0'
      previewFrame.src = `${baseUrl}?reload=${Date.now()}`
    }
    window.postMessage({ type: 'reload-preview-page' }, '*')
    setTimeout(() => {
      get().applyClientOverrides()
    }, 600)
  },
  clearPendingBuild: () => {
    const state = get()
    if (state.pendingBuild?.status === 'ready') {
      state.clearClientOverrides()
    }
    set({ pendingBuild: undefined })
  },
  rebuildCurrentVersion: async (projectId: string, versionIndex: number) => {
    try {
      const buildId = generateShortId(projectId)

      // Set pending build status immediately
      set({
        pendingBuild: {
          id: buildId,
          version: versionIndex,
          status: 'building',
          updatedAt: new Date().toISOString(),
          error: null,
        },
      })

      // Call the build API
      const response = await fetch(`/api/projects/${projectId}/build`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          versionIndex,
        }),
      })

      if (!response.ok) {
        // Surface the real server-side build error instead of a generic
        // "Build request failed". The /build route returns the underlying
        // serve-build error (e.g. "Failed to fetch project data",
        // "White screen check failed", pnpm/vite build output) in the JSON
        // body; swallowing it here is why "Build request failed (version N)"
        // told nobody what actually broke.
        let serverError = ''
        try {
          const errBody = await response.clone().json()
          serverError =
            errBody?.error || errBody?.message || errBody?.key || ''
        } catch {
          try {
            serverError = (await response.text())?.slice(0, 500) || ''
          } catch {
            serverError = ''
          }
        }
        throw new Error(
          serverError
            ? `Build request failed (v${versionIndex}): ${serverError}`
            : `Build request failed (v${versionIndex}, status ${response.status})`,
        )
      }

      const data = await response.json()

      if (data.status === 'success' && data.previewUrl) {
        // Update project store with new versions
        const projectStore = useProjectStore.getState()
        if (projectStore.project && data.versions) {
          projectStore.updateProject({ versions: data.versions })
        }

        // Clear client overrides before setting new preview URL
        get().clearClientOverrides()

        // Update preview URL and mark build as ready
        set({
          previewUrl: data.previewUrl,
          pendingBuild: {
            id: buildId,
            version: versionIndex,
            status: 'ready',
            updatedAt: new Date().toISOString(),
            error: null,
          },
        })

        // Reload the preview page
        get().reloadPreviewPage()
      } else {
        throw new Error('Build succeeded but no preview URL returned')
      }
    } catch (error: any) {
      console.error('[rebuildCurrentVersion] Failed:', error)
      set({
        pendingBuild: {
          id: generateShortId(projectId),
          version: versionIndex,
          status: 'failed',
          updatedAt: new Date().toISOString(),
          error: error.message || 'Rebuild failed',
        },
      })
      alertError(
        'Failed to rebuild preview. Please try again.',
        error?.message,
      )
    }
  },
  setClientOverrides: (overrides: EditorChangeContent[]) => {
    console.log(
      '🟣 [PREVIEW] setClientOverrides called with:',
      overrides.length,
      'overrides',
    )
    if (typeof window !== 'undefined') {
      const existingInterval = get().overrideApplyInterval
      if (existingInterval) {
        console.log('🟣 [PREVIEW] Clearing existing interval')
        window.clearInterval(existingInterval)
        set({ overrideApplyInterval: undefined })
      }
    }

    set({ clientOverrides: overrides })
    const apply = get().applyClientOverrides

    if (overrides.length === 0) {
      console.log('🟣 [PREVIEW] No overrides to apply, returning')
      return
    }

    console.log('🟣 [PREVIEW] Applying overrides immediately')
    apply()

    requestAnimationFrame(() => {
      apply()
    })

    const retryDelays = [200, 600, 1200, 2000]

    retryDelays.forEach((delay) => {
      setTimeout(() => {
        apply()
      }, delay)
    })

    if (typeof window !== 'undefined') {
      const interval = window.setInterval(() => {
        apply()
      }, 1500)

      set({ overrideApplyInterval: interval })
    }
  },
  applyClientOverrides: () => {
    const overrides = get().clientOverrides
    console.log(
      '🟣 [PREVIEW] applyClientOverrides called, overrides:',
      overrides?.length || 0,
    )
    if (!overrides?.length) {
      console.log('🟣 [PREVIEW] No overrides to apply')
      return
    }

    const iframe = document.getElementById(
      'project-preview',
    ) as HTMLIFrameElement | null

    if (!iframe?.contentWindow) {
      console.log('🟣 [PREVIEW] No iframe or contentWindow found')
      return
    }

    console.log(
      '🟣 [PREVIEW] Applying',
      overrides.length,
      'overrides to iframe',
    )
    overrides.forEach((change) => {
      if (!change?.type || !change?.data) return
      const normalizedType = removeAfterfix(
        removeAfterfix(change.type, '__desktop'),
        '__mobile',
      )

      console.log(
        '🟣 [PREVIEW] Sending override:',
        normalizedType,
        change.data.id,
      )
      try {
        iframe.contentWindow?.postMessage(
          {
            type: normalizedType,
            data: change.data,
          },
          '*',
        )
      } catch (error) {
        console.error('[preview] failed to apply client override', error)
      }
    })
  },
  clearClientOverrides: () => {
    if (typeof window !== 'undefined') {
      const existingInterval = get().overrideApplyInterval
      if (existingInterval) {
        window.clearInterval(existingInterval)
      }
    }

    set({ clientOverrides: [], overrideApplyInterval: undefined })
  },
  waitForBuild: async ({
    projectId,
    version,
    buildId,
    pollIntervalMs = 3_000, //per 3 seconds
    maxAttempts = 30, // Total 300 seconds timeout
    skipReload = false, // Skip iframe reload when build completes (for edit mode)
  }) => {
    if (!projectId || !buildId) return

    set({
      pendingBuild: {
        id: buildId,
        version,
        status: 'building',
        updatedAt: new Date().toISOString(),
        error: null,
      },
    })

    const delay = (ms: number) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms)
      })

    const pollStatus = async (attempt = 0): Promise<void> => {
      if (attempt > maxAttempts) {
        // Keep overrides visible so user doesn't lose their changes
        // Notify user with retry option
        toast.error('Sync timed out', {
          action: {
            label: 'Retry',
            onClick: () => {
              get().rebuildCurrentVersion(projectId, version)
            },
          },
        })

        set({
          pendingBuild: {
            id: buildId,
            version,
            status: 'failed',
            updatedAt: new Date().toISOString(),
            error: 'Timed out waiting for build completion.',
          },
        })
        return
      }

      try {
        const response = await fetch(
          `/api/me/project/${projectId}/build-status?version=${version}&buildId=${buildId}`,
          {
            cache: 'no-store',
          },
        )

        if (!response.ok) {
          throw new Error(`Status code ${response.status}`)
        }

        const {
          data,
        }: {
          data: {
            status: 'building' | 'ready' | 'failed' | 'superseded' | 'idle'
            previewUrl?: string
            hash?: string
            error?: string | null
            updatedAt?: string | null
            buildId?: string | null
            activeBuildId?: string | null
          }
        } = await response.json()

        if (data.status === 'building') {
          set({
            pendingBuild: {
              id: buildId,
              version,
              status: 'building',
              updatedAt: data.updatedAt || new Date().toISOString(),
              error: null,
            },
          })
          await delay(pollIntervalMs)
          return pollStatus(attempt + 1)
        }

        if (data.status === 'ready') {
          const projectStore = useProjectStore.getState()
          const project = projectStore.project
          if (project) {
            const versionKey = `${version}`
            const nextVersions = {
              ...(project.versions || {}),
              [versionKey]: {
                ...(project.versions?.[versionKey] || { id: versionKey }),
                previewUrl:
                  data.previewUrl || project.versions?.[versionKey]?.previewUrl,
                hash: data.hash || project.versions?.[versionKey]?.hash,
                buildStatus: 'ready' as const,
                buildError: '',
                buildId: null,
                buildUpdatedAt:
                  data.updatedAt ||
                  project.versions?.[versionKey]?.buildUpdatedAt,
              },
            }
            projectStore.updateProject({ versions: nextVersions })
          }

          if (data.previewUrl) {
            set({
              previewUrl: data.previewUrl,
              pendingBuild: {
                id: buildId,
                version,
                status: 'ready',
                updatedAt: data.updatedAt || new Date().toISOString(),
                error: null,
              },
            })
          } else {
            set({
              pendingBuild: {
                id: buildId,
                version,
                status: 'ready',
                updatedAt: data.updatedAt || new Date().toISOString(),
                error: null,
              },
            })
          }

          // Skip reload for edit mode - client overrides already showing the changes
          if (skipReload) {
            // Keep overrides visible, don't clear them until next page reload or AI coding
            // This prevents the iframe from showing stale content
            return
          }

          // For AI coding and other flows, reload to show the new build
          get().clearClientOverrides()
          get().reloadPreviewPage()
          return
        }

        if (data.status === 'failed') {
          const projectStore = useProjectStore.getState()
          const project = projectStore.project
          if (project) {
            const versionKey = `${version}`
            const nextVersions = {
              ...(project.versions || {}),
              [versionKey]: {
                ...(project.versions?.[versionKey] || { id: versionKey }),
                buildStatus: 'failed' as const,
                buildError: data.error || 'Build failed',
                buildId: data.buildId || buildId,
                buildUpdatedAt:
                  data.updatedAt ||
                  project.versions?.[versionKey]?.buildUpdatedAt,
              },
            }
            projectStore.updateProject({ versions: nextVersions })
          }

          // Keep overrides visible so user doesn't lose their changes
          // They can still see what they edited

          // Notify user with retry option
          toast.error('Failed to sync changes', {
            action: {
              label: 'Retry',
              onClick: () => {
                get().rebuildCurrentVersion(projectId, version)
              },
            },
          })

          set({
            pendingBuild: {
              id: buildId,
              version,
              status: 'failed',
              updatedAt: data.updatedAt || new Date().toISOString(),
              error: data.error || 'Build failed',
            },
          })
          return
        }

        if (data.status === 'superseded') {
          set({
            pendingBuild: {
              id: data.activeBuildId || buildId,
              version,
              status: 'superseded',
              updatedAt: data.updatedAt || new Date().toISOString(),
              error: null,
            },
          })
          return
        }

        // idle or unknown state
        set({
          pendingBuild: {
            id: buildId,
            version,
            status: data.status,
            updatedAt: data.updatedAt || new Date().toISOString(),
            error: data.error || null,
          },
        })
      } catch (error) {
        console.error('[preview] build status polling failed', error)
        await delay(pollIntervalMs)
        return pollStatus(attempt + 1)
      }
    }

    await pollStatus(0)
  },
  setProcessingText: (text: string) => {
    set({ processingText: text })
  },
  updateScreenshot: async () => {
    const { project } = useProjectStore.getState()
    if (!project) return
    const previewUrl = get().previewUrl
    if (!previewUrl) return
    const filename = `${generateShortId(project._id.toString())}/thumbnail-${Date.now()}.png`
    const response = await fetch('/api/screenshot', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        url: previewUrl,
        filename,
        prompt: project.project_name,
      }),
    })
    const data = await response.json()
    const formData = new FormData()
    if (data?.url) {
      formData.append('projectId', project._id.toString())
      formData.append('thumbnail', data.url)
      await fetch('/api/me/project', {
        method: 'POST',
        body: formData,
      })
      useProjectStore.getState().updateProject({ thumbnail: data.url })
    }
  },
  updateOpenGraphImage: async () => {
    const { project } = useProjectStore.getState()
    if (!project || !project.thumbnail) return
    const response = await fetch(
      `/api/projects/${project._id}/open-graph-image`,
      {
        method: 'POST',
      },
    )
    const data = await response.json()
    if (data?.og_image) {
      useProjectStore.getState().updateProject({
        og_image: data.og_image,
      })
    }
  },
  setShowPreview: (show: boolean) => set({ showPreview: show }),
  setStage: (
    stage:
      | 'idle'
      | 'installing'
      | 'starting'
      | 'running'
      | 'error'
      | 'runtime_error',
  ) => set({ stage }),
  autoFixIssueTime: 0,
  increaseAutoFixIssueTime: () =>
    set({ autoFixIssueTime: get().autoFixIssueTime + 1 }),
  canAutoFixIssue: () => get().autoFixIssueTime < AUTO_FIX_ISSUE_TIME_LIMIT,
}))

declare global {
  interface Window {
    __heybossPreviewOverridesListener?: boolean
  }
}

const handleEditorLifecycleMessage = (event: MessageEvent) => {
  const { data } = event
  if (!data || typeof data !== 'object') return

  if (!['editor.loaded', 'reload-preview-page'].includes(data.type)) return

  const state = usePreviewStore.getState()

  // When iframe reports loaded, restore stage to 'running' and show iframe
  if (data.type === 'editor.loaded') {
    state.setStage('running')
    // Show iframe after it's loaded
    const previewUrl = state.previewUrl
    if (previewUrl) {
      const baseUrl = previewUrl.split('?')[0]
      const previewFrame = document.querySelector(
        `iframe[src^="${baseUrl}"]`,
      ) as HTMLIFrameElement
      if (previewFrame) {
        previewFrame.style.opacity = '1'
      }
    }
  }

  if (!state.clientOverrides.length) return

  state.applyClientOverrides()
}

if (
  typeof window !== 'undefined' &&
  !window.__heybossPreviewOverridesListener
) {
  window.__heybossPreviewOverridesListener = true
  window.addEventListener('message', handleEditorLifecycleMessage)
}
